if (!customElements.get('measuring-tape-scroll')) {
  customElements.define('measuring-tape-scroll', class MeasuringTapeScroll extends HTMLElement {

    static PX_PER_CM = 37.795;
    static PX_PER_MM = 37.795 / 10;
    static PX_PER_IN = 96;

    connectedCallback() {
      this._tape             = this.querySelector('.mt-tape');
      this._rafId            = null;
      this._active           = false;
      this._lastScrollHeight = 0;

      this._setupResizeObserver();
      this._init();
    }

    disconnectedCallback() {
      this._destroy();
      if (this._resizeObserver) this._resizeObserver.disconnect();
    }

    // ── Init / destroy ──────────────────────────────────────────────────────

    _init() {
      this._lastScrollHeight = document.documentElement.scrollHeight;
      this._generateTape();
      this._active = true;
      this._startScrollLoop();
    }

    _destroy() {
      this._stopScrollLoop();
      if (this._tape) this._tape.innerHTML = '';
      this._active = false;
    }

    _regenerate() {
      this._stopScrollLoop();
      this._tape.innerHTML = '';
      this._lastScrollHeight = document.documentElement.scrollHeight;
      this._generateTape();
      this._startScrollLoop();
    }

    // ── Tape generation ─────────────────────────────────────────────────────

    _generateTape() {
      const PX_PER_CM    = MeasuringTapeScroll.PX_PER_CM;
      const PX_PER_MM    = MeasuringTapeScroll.PX_PER_MM;
      const scrollHeight = document.documentElement.scrollHeight;
      const totalCm      = Math.ceil(scrollHeight / PX_PER_CM);

      const frag = document.createDocumentFragment();

      for (let cm = 0; cm <= totalCm; cm++) {
        const cmTop = cm * PX_PER_CM;

        // Centimetre tick mark
        const cmTick = document.createElement('div');
        cmTick.className = 'mt-tick mt-tick--cm';
        cmTick.style.top = cmTop + 'px';
        frag.appendChild(cmTick);

        // Centimetre label (skip 0)
        if (cm > 0) {
          const label = document.createElement('span');
          label.className = cm % 10 === 0
            ? 'mt-label mt-label--milestone'
            : 'mt-label';
          label.style.top = cmTop + 'px';
          label.textContent = cm;
          frag.appendChild(label);
        }

        // Millimetre ticks (1–9 within this centimetre)
        for (let mm = 1; mm <= 9; mm++) {
          const mmTick = document.createElement('div');
          mmTick.className = mm === 5
            ? 'mt-tick mt-tick--half'
            : 'mt-tick mt-tick--mm';
          mmTick.style.top = (cmTop + mm * PX_PER_MM) + 'px';
          frag.appendChild(mmTick);
        }
      }

      // ── Inch scale (right edge, desktop only via CSS) ────────────────────
      const PX_PER_IN = MeasuringTapeScroll.PX_PER_IN;
      const totalIn   = Math.ceil(scrollHeight / PX_PER_IN);

      for (let inch = 0; inch <= totalIn; inch++) {
        const inTop = inch * PX_PER_IN;

        const inTick = document.createElement('div');
        inTick.className = 'mt-tick mt-tick--in';
        inTick.style.top = inTop + 'px';
        frag.appendChild(inTick);

        if (inch > 0) {
          const inLabel = document.createElement('span');
          inLabel.className = inch % 12 === 0
            ? 'mt-label mt-label--in mt-label--milestone'
            : 'mt-label mt-label--in';
          inLabel.style.top = inTop + 'px';
          inLabel.textContent = inch;
          frag.appendChild(inLabel);
        }

        if (inch < totalIn) {
          const halfTick = document.createElement('div');
          halfTick.className = 'mt-tick mt-tick--in-half';
          halfTick.style.top = (inTop + PX_PER_IN / 2) + 'px';
          frag.appendChild(halfTick);

          [1, 3].forEach(q => {
            const qTick = document.createElement('div');
            qTick.className = 'mt-tick mt-tick--in-qtr';
            qTick.style.top = (inTop + PX_PER_IN * q / 4) + 'px';
            frag.appendChild(qTick);
          });

          [1, 3, 5, 7].forEach(e => {
            const eTick = document.createElement('div');
            eTick.className = 'mt-tick mt-tick--in-eighth';
            eTick.style.top = (inTop + PX_PER_IN * e / 8) + 'px';
            frag.appendChild(eTick);
          });
        }
      }

      this._tape.style.height = scrollHeight + 'px';
      this._tape.appendChild(frag);
    }

    // ── Scroll loop ─────────────────────────────────────────────────────────

    _startScrollLoop() {
      const update = () => {
        this._tape.style.transform = `translateY(-${window.scrollY}px)`;
        this._rafId = requestAnimationFrame(update);
      };
      this._rafId = requestAnimationFrame(update);
    }

    _stopScrollLoop() {
      if (this._rafId) {
        cancelAnimationFrame(this._rafId);
        this._rafId = null;
      }
    }

    // ── Resize observer ─────────────────────────────────────────────────────

    _setupResizeObserver() {
      this._resizeObserver = new ResizeObserver(() => {
        const scrollHeight = document.documentElement.scrollHeight;

        if (!this._active) {
          this._init();
        } else if (scrollHeight !== this._lastScrollHeight) {
          this._lastScrollHeight = scrollHeight;
          this._regenerate();
        }
      });

      this._resizeObserver.observe(document.documentElement);
    }

  });
}
